import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AuthPanel,
  AuthHeader,
  AUTH_NAME_STEP,
  type AuthResult,
} from '@/components/auth';
import { AuthAction } from '@/types/gql.generated';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@/ui';
import { ActionText } from './components/ActionText';
import { useRegisterModalStore } from './store';

export const RegisterModal = () => {
  const { t } = useTranslation('auth');

  const {
    isOpen,
    style,
    action,
    launch,
    defaults,
    close,
    onCancel,
    onAuthenticated,
  } = useRegisterModalStore();

  const [isNameStep, setIsNameStep] = useState(false);

  const handleAuthenticated = (result: AuthResult) => {
    setIsNameStep(false);
    close();
    onAuthenticated?.(result);
  };

  const handleCancel = () => {
    // Same reset as handleAuthenticated: the panel remounts on reopen and reports
    // its step in an effect, so without this the modal's first frame is missing
    // its header.
    setIsNameStep(false);
    close();
    onCancel?.();
  };

  let redirectTo = `${location.pathname}${location.search}`;
  if (launch) {
    redirectTo += location.search.startsWith('?') ? '&' : '?';
    redirectTo += `launch=${launch}`;
  }

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      returnFocusOnClose={false}
      variant="primary"
      onClose={handleCancel}
    >
      <ModalOverlay />

      <ModalContent
        data-testid="register-modal"
        minW={{ base: '100vw', md: '30rem' }}
      >
        {/* Suppressed on the name step: by then the user is logged in, so a
            "log in or create an account" title contradicts the panel below it.
            The panel supplies its own header for that step. */}
        {style !== 'inline' && !isNameStep && (
          <ModalHeader>{t('sign_in_or_create')}</ModalHeader>
        )}
        <ModalCloseButton />

        <ModalBody display="flex" flexDir="column" gap="6">
          {!isNameStep && <ActionText action={action} variant={style} />}
          <AuthPanel
            defaults={defaults}
            fullWidth
            hideLogo
            redirectTo={redirectTo}
            renderHeader={({ authAction }) => {
              if (authAction === AuthAction.LoginPassword) {
                return <AuthHeader>{t('sign_into_account')}</AuthHeader>;
              }
              if (authAction === AuthAction.LoginGoogle) {
                return <AuthHeader>{t('sign_in_with_google')}</AuthHeader>;
              }
              if (authAction === AuthAction.LoginApple) {
                return <AuthHeader>{t('sign_in_with_apple')}</AuthHeader>;
              }
              if (authAction === AuthAction.LoginEmailCode) {
                return <AuthHeader title={t('check_your_email')} />;
              }
              return null;
            }}
            onAuthenticated={handleAuthenticated}
            onStepChange={(step) => setIsNameStep(step === AUTH_NAME_STEP)}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
